import React, {
    Ref,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
    KeyboardEvent,
    ReactNode,
} from "react";
import { MenuItem, MenuList, Paper, Theme, Typography, makeStyles } from "@material-ui/core";
import { ICellEditorParams } from "ag-grid-community";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { cancelEvent } from "containers/legacy-cart/helpers/common";
import { IOption } from "constants/types";
import MediaConfirmation from "components/dialog/media-confirmation";
import _ from "lodash";

const DEFAULT_DROPDOWN_HEIGHT = 150;

type Option<T> = IOption<string | number | boolean> & { data: T };

export type SelectEditorProps<T> = ICellEditorParams & {
    value: T;
    values: Option<T>[];
    dropdownHeight?: number;
    confirmation?: { message: ReactNode; title?: string };
};

export default forwardRef(SelectEditor);

function SelectEditor<T>(props: SelectEditorProps<T>, ref: Ref<{ getValue: () => any }>) {
    const { value, values, formatValue, stopEditing, colDef } = props;
    const { confirmation, dropdownHeight = DEFAULT_DROPDOWN_HEIGHT } = props;

    const [confirmed, setConfirmed] = useState(!confirmation);
    const [newValue, setNewValue] = useState<Option<T>>(null);

    const handleSelect = useCallback(
        (newValue: Option<T>) => {
            setNewValue(newValue);
            setConfirmed((confirmed) => confirmed || newValue.value === formatValue(value));
        },
        [formatValue, value],
    );

    const handleValueClick = useCallback((value: Option<T>) => () => handleSelect(value), [handleSelect]);
    const handleEnterDown = useCallback(
        (value: Option<T>) => (e: KeyboardEvent<HTMLLIElement>) => {
            if (e.key === KEYBOARD_KEYS.ENTER) {
                cancelEvent(e);
                handleSelect(value);
            }
        },
        [handleSelect],
    );

    const handleConfirm = useCallback(() => setConfirmed(true), []);
    const handleDiscard = useCallback(() => stopEditing(), [stopEditing]);

    useImperativeHandle(ref, () => ({
        getValue: () => (confirmed && newValue ? newValue.data : (value?.value ?? value)),
    }));

    useEffect(() => {
        if (confirmed && newValue) {
            stopEditing();
        }
    }, [confirmed, newValue, stopEditing]);

    const classes = useStyles({ width: colDef.width, height: dropdownHeight });
    return (
        <Paper className={classes.wrapper}>
            <MenuList autoFocus={true}>
                {values.map((option) => (
                    <MenuItem
                        key={_.toString(option.value)}
                        value={_.toString(option.value)}
                        selected={option.value === formatValue(value)}
                        onClick={handleValueClick(option)}
                        onKeyDown={handleEnterDown(option)}
                        className={classes.item}
                    >
                        <Typography variant="inherit" noWrap={true}>
                            {option.label}
                        </Typography>
                    </MenuItem>
                ))}
                {!!confirmation && (
                    <MediaConfirmation
                        open={!confirmed && !!newValue}
                        title={confirmation.title}
                        onSave={handleConfirm}
                        onClose={handleDiscard}
                        disablePortal={true} // do not loose focus from cell when modal is clicked
                    >
                        {confirmation.message}
                    </MediaConfirmation>
                )}
            </MenuList>
        </Paper>
    );
}

const useStyles = makeStyles<Theme, { width: number; height: number }>({
    wrapper: ({ width, height }) => ({
        width,
        maxHeight: height,
        overflow: "auto",
    }),
    item: {
        fontFamily: "Lato, sans-serif",
        fontSize: 14,
        // to show empty item
        minHeight: 33,
    },
});
